import { initializeApp } from "firebase/app";
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  getAuth,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as fbSignOut,
} from "firebase/auth";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);

/**
 * Comma-separated list of allowed email domains. Falls back to the legacy
 * singular VITE_ALLOWED_EMAIL_DOMAIN. Lower-cased and trimmed.
 */
export const ALLOWED_DOMAINS: string[] = (
  (import.meta.env.VITE_ALLOWED_EMAIL_DOMAINS as string | undefined) ??
  (import.meta.env.VITE_ALLOWED_EMAIL_DOMAIN as string | undefined) ??
  "chicagoventure.com"
)
  .split(",")
  .map((s) => s.trim().toLowerCase())
  .filter(Boolean);

/**
 * On-domain accounts with no access to this tool; has Beacon only. Treated
 * exactly like a wrong-domain account (signed out by the auth gate). Same list
 * as env.deniedEmails in the backend and isMember() in firestore.rules.
 */
const DENIED_EMAILS: readonly string[] = ["slynch@chicagoventure.com"];

export function isAllowedEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  const lc = email.toLowerCase();
  return ALLOWED_DOMAINS.some((d) => lc.endsWith(`@${d}`)) && !DENIED_EMAILS.includes(lc);
}

function preferredGoogleHd(): string {
  return (
    ALLOWED_DOMAINS.find((d) => d.includes("chicagoventure")) ?? ALLOWED_DOMAINS[0] ?? ""
  );
}

export async function signInWithGoogle(): Promise<void> {
  const provider = new GoogleAuthProvider();
  const hd = preferredGoogleHd();
  provider.setCustomParameters({
    ...(hd ? { hd } : {}),
    prompt: "select_account",
  });
  await signInWithPopup(auth, provider);
}

/**
 * Email/password sign-in. The backend additionally requires email_verified
 * = true and the email domain to match an entry in ALLOWED_DOMAINS, so an
 * unverified or off-domain user will sign in here but be rejected by the
 * /api/* endpoints.
 */
export async function signInWithEmail(email: string, password: string): Promise<void> {
  await signInWithEmailAndPassword(auth, email, password);
}

export async function signUpWithEmail(
  email: string,
  password: string,
  displayName?: string,
): Promise<void> {
  if (!isAllowedEmail(email)) {
    throw new Error(
      `Sign-up is restricted to ${ALLOWED_DOMAINS.map((d) => "@" + d).join(" or ")} emails.`,
    );
  }
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  if (displayName) {
    // Attach the displayName immediately so the backend captures it on first
    // /api/me ping and bakes it into newly-created deals/messages.
    const { updateProfile } = await import("firebase/auth");
    await updateProfile(cred.user, { displayName });
  }
  await sendEmailVerification(cred.user);
}

export async function resendVerificationEmail(): Promise<void> {
  if (!auth.currentUser) throw new Error("not_signed_in");
  await sendEmailVerification(auth.currentUser);
}

/**
 * Re-check whether the signed-in user has verified their email since the page
 * loaded. `reload()` pulls the latest verification state, and `getIdToken(true)`
 * forces a fresh ID token so the backend immediately sees `email_verified`
 * (without this, the cached token keeps reporting unverified for up to an hour
 * after the user clicks the link). Returns the post-reload verified state.
 */
export async function refreshEmailVerified(): Promise<boolean> {
  if (!auth.currentUser) return false;
  await auth.currentUser.reload();
  await auth.currentUser.getIdToken(true);
  return auth.currentUser.emailVerified;
}

export async function sendPasswordReset(email: string): Promise<void> {
  await sendPasswordResetEmail(auth, email);
}

export async function signOut(): Promise<void> {
  await fbSignOut(auth);
}
